import { useAppDispatch, useAppSelector } from 'common/hooks/redux';
import { getChartJson, WizardStepProps } from '../PowerpointExportWizard';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { selectEndDate, selectStartDate } from 'common/store/slices/date-filter.slice';
import { RootState } from 'common/store';
import { API, API_PREFIX, apiUrlFor, DATASET_INITIAL_ID } from 'common/constants';
import { useWidgetFactory } from 'common/hooks/widgets';
import { WidgetModel } from 'common/models/widget.model';
import { MultiExportQuestionList } from 'common/models/pptx.model';
import { SurveyResult, useGetSurveyQuestionsQuery } from 'common/store/api/question.api';
import {
  TreeView,
  TreeViewCheckChangeEvent,
  TreeViewExpandChangeEvent,
} from '@progress/kendo-react-treeview';
import { QuestionModel } from 'common/models/dataset.model';
import { showToast } from 'common/store/slices/notification.slice';
import { NotificationType } from 'common/models/notification.model';
import { Typography } from '@progress/kendo-react-common';
import { FlyoutBase, FlyoutBody } from '../Flyout';
import { KWELLO_ICON, KwelloSvgIcon } from 'common/components/icon/KwelloSvgIcon';
import { setWizardStep } from 'common/store/slices/export-ppt.slice';
import { Tooltip } from 'react-tooltip';
import { AppliedFilters } from 'common/components/layouts/filters/AppliedFilters';
import {
  Checkbox,
  Switch,
  SwitchChangeEvent,
  TextBox,
  TextBoxChangeEvent,
} from '@progress/kendo-react-inputs';
import Footer from './Footer';
import useFeatureFlags from 'common/hooks/useFeatureFlags';
import { Feature, Claim } from 'common/models/page-config.models';
import useClaims from 'common/hooks/useClaims';
import { Spinner } from 'common/components/loader/Spinner';
import useFilterLabels, { FilterLabelItem } from 'common/hooks/useFilterLabels';
import { FlyoutHeader } from '../components/FlyoutHeader';
import { useCompareUrlWithProjectId } from 'common/hooks/route';
import { ALIGNMENT_ROUTES } from 'routes/routes.const';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';
import { useQuestionExportPPT } from '../../widgets/widget-hooks/useClientSideExportPPT';
import { WizardStep } from '../../../models/pptx.model';
import HoverableSpan from 'common/components/HoverableSpan';
import { selectChartFilters } from 'common/store/slices/chart-filters.slice';
export interface SimpleFilter {
  property: string;
  value: string | number | null;
}

interface TreeItem {
  id: number;
  name: string;
  questions: QuestionModel[];
  expanded: boolean;
  selected: boolean;
}

export const getLeftChartJson = (isAlignment: boolean) => {
  const chartId = isAlignment ? 'alignment_detail_overview' : 'response_overview';
  const chartWidget: WidgetModel = getChartJson(chartId, isAlignment);
  return chartWidget;
};
const FILTER_LABEL_TO_PROPERTY: Record<string, string> = {
  Category: 'category',
  Question: 'question',
  Profile: 'profile',
};
export const getRightChartJson = (isAlignment: boolean) => {
  const chartId = isAlignment ? 'alignment_response_volume' : 'insight_detail_response_volume';
  const chartWidget: WidgetModel = getChartJson(chartId, isAlignment);
  return chartWidget;
};

export const getSummaryJson = (isAlignment: boolean) => {
  const chartId = isAlignment ? 'alignment_detail_counters' : 'insight_detail_counters';
  const chartWidget: WidgetModel = getChartJson(chartId, isAlignment);
  return chartWidget;
};

const addIsAlignmentFlag = (
  data: SurveyResult[] | undefined,
  isAlignment: boolean
): SurveyResult[] => {
  if (!data) {
    return [];
  }

  return data.map((item) => ({
    ...item,
    questions: item.questions.map((question) => ({ ...question, isAlignment })),
  }));
};

// Updated CustomTreeItem component
const CustomTreeItem = (props: any) => {
  const { item } = props;
  const isDisabled = item.disabled;

  if (isDisabled) {
    const tooltipId = `disabled-tip-${item.id}`;
    return (
      <HoverableSpan
        tooltipId={tooltipId}
        tooltipContent={
          <div style={{ lineHeight: '1.4' }}>
            <strong style={{ display: 'block', marginBottom: '4px' }}>
              This item is unavailable based on your current filters.
            </strong>
            If you would like to select this item please adjust your applied page filters.
          </div>
        }
        disabled
      >
        {item.name}
      </HoverableSpan>
    );
  }

  return <span>{item.name}</span>;
};

function Question({ onClose }: Readonly<WizardStepProps>) {
  const dispatch = useAppDispatch();
  const { hasFeature } = useFeatureFlags();
  const { hasClaim } = useClaims();
  const [mounted, setMounted] = useState<boolean>(false);
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [includeOpposite, setIncludeOpposite] = useState(false);
  const [tree, setTree] = useState<TreeItem[]>([]);
  const [expanded, setExpanded] = useState<number[]>([]);
  const [selected, setSelected] = useState<number[]>([]);

  const filterLabels = useFilterLabels();
  const { compareUrl } = useCompareUrlWithProjectId();
  const { exportMultiQuestionPPT } = useQuestionExportPPT();
  const startDate = useAppSelector((state: RootState) =>
    selectStartDate(state, DATASET_INITIAL_ID)
  );
  const endDate = useAppSelector((state: RootState) => selectEndDate(state, DATASET_INITIAL_ID));
  const chartFilters = useAppSelector((state: RootState) =>
    selectChartFilters(state, DATASET_INITIAL_ID)
  );
  const { activeProject } = useModuleProjectContext();
  const [exportingData, setExportingData] = useState(false);

  const isAlignmentPath = compareUrl(ALIGNMENT_ROUTES.root, { includes: true });
  const hasBothInsightsAlignmentClaims = hasClaim([Claim.Alignment, Claim.Insights], true);
  const questionsList = useMemo(() => tree.map((item) => item.questions).flat(), [tree]);
  const leftChart = useWidgetFactory({
    ...getLeftChartJson(true),
    datasetId: DATASET_INITIAL_ID,
  });

  const rightChart = useWidgetFactory({
    ...getRightChartJson(true),
    datasetId: DATASET_INITIAL_ID,
  });
  const summaryWidget = useWidgetFactory({
    ...getSummaryJson(true),
    datasetId: DATASET_INITIAL_ID,
  });

  const oppositeLeftChart = useWidgetFactory({
    ...getLeftChartJson(false),
    datasetId: DATASET_INITIAL_ID,
  });

  const oppositeRightChart = useWidgetFactory({
    ...getRightChartJson(false),
    datasetId: DATASET_INITIAL_ID,
  });
  const oppositeSummaryWidget = useWidgetFactory({
    ...getSummaryJson(false),
    datasetId: DATASET_INITIAL_ID,
  });

  const allSelected = useMemo(() => {
    const enabledQuestions = questionsList.filter((q) => !q.isDisabled);
    const enabledIds = enabledQuestions.map((q) => q.id);

    return enabledIds.length > 0 && enabledIds.every((id) => selected.includes(id));
  }, [questionsList, selected]);

  const toggleQuestionType =
    hasFeature(Feature.MultipleQuestionExportToggleQuestionType) && hasBothInsightsAlignmentClaims;

  const skipInsights = isAlignmentPath && !includeOpposite;
  const skipAlignment = !isAlignmentPath && !includeOpposite;

  useEffect(() => {
    // Remove selected id's for questions that are no longer in the list
    const selectedToRemove = selected.filter(
      (id) => !questionsList.find((question) => question.id === id)
    );

    if (selectedToRemove.length > 0) {
      setSelected((prev) => prev.filter((id) => !selectedToRemove.includes(id)));
    }
  }, [questionsList, includeOpposite, selected]);

  const insightsQuery = useGetSurveyQuestionsQuery(
    {
      startDate,
      endDate,
      projectId: activeProject?.moduleProjectID,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: skipInsights,
    }
  );

  const alignmentQuery = useGetSurveyQuestionsQuery(
    {
      request: API.ALIGNMENT,
      startDate,
      endDate,
      projectId: activeProject?.moduleProjectID,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: skipAlignment,
    }
  );

  const data = useMemo(() => {
    const insightsData = !skipInsights ? addIsAlignmentFlag(insightsQuery.data, false) : [];
    const alignmentData = !skipAlignment ? addIsAlignmentFlag(alignmentQuery.data, true) : [];

    const combinedData = [...insightsData, ...alignmentData];

    const filteredData = combinedData.reduce((acc: SurveyResult[], item: SurveyResult) => {
      const filteredQuestions = item.questions.filter((question) =>
        question.name.toLowerCase().includes(searchFilter.toLowerCase())
      );

      if (filteredQuestions.length > 0) {
        acc.push({ ...item, questions: filteredQuestions });
      }

      return acc;
    }, []);

    return filteredData;
  }, [insightsQuery.data, alignmentQuery.data, skipInsights, skipAlignment, searchFilter]);

  useEffect(() => {
    const alignmentsReady = ['fulfilled', 'uninitialized'].includes(alignmentQuery.status);
    const insightsReady = ['fulfilled', 'uninitialized'].includes(insightsQuery.status);

    if (!mounted && alignmentsReady && insightsReady && questionsList.length > 0) {
      setSelected(questionsList.map((question: QuestionModel) => question.id));
      setMounted(true);
    }
  }, [questionsList, mounted, alignmentQuery.status, insightsQuery.status]);

  const onExpandChange = (ev: TreeViewExpandChangeEvent) => {
    const { item } = ev;
    const isExpanded = expanded.includes(item.id);

    if (isExpanded) {
      const nextExpanded = [...expanded];
      const index = nextExpanded.findIndex((id) => item.id === id);
      if (index >= 0) {
        nextExpanded.splice(index, 1);
        setExpanded(nextExpanded);
      }
    } else {
      setExpanded([...expanded, ...[item.id]]);
    }
  };

  const onSelectionChange = (ev: TreeViewCheckChangeEvent) => {
    const { item } = ev;
    const isParent = !item.parent;
    const isSelected = item.selected;

    if (isParent && !isSelected) {
      const childrenToSelect = item.questions
        .filter((question: QuestionModel) => !question.selected)
        .map((question: QuestionModel) => question.id);

      return setSelected([...selected, ...childrenToSelect]);
    }

    if (isParent && isSelected) {
      const childrenToDeselect = item.questions.map((question: QuestionModel) => question.id);

      return setSelected((prev) => prev.filter((id) => !childrenToDeselect.includes(id)));
    }

    if (!isParent && !isSelected) {
      return setSelected([...selected, item.id]);
    }

    if (!isParent && isSelected) {
      return setSelected((prev) => prev.filter((id) => id !== item.id));
    }
  };

  const questionsForExport = useCallback((): MultiExportQuestionList[] => {
    return questionsList
      .filter((question: QuestionModel) => question.selected)
      .map((question: QuestionModel) => ({
        questionId: question.id,
        surveyTitle: question.parent.name,
        questionText: question.name,
        isAlignment: question.isAlignment,
        leftChartGrouping: getLeftChartGrouping(question.isAlignment || false),
        rightChartGrouping: getRightChartGrouping(question.isAlignment || false),
      }));
  }, [questionsList]);

  const getLeftChartGrouping = (isAlignment: boolean) => {
    return [
      {
        member: isAlignment ? 'AlignmentScore' : 'ResponseOption',
        sortDirection: 0,
        displayContent: 'Key',
      },
      {
        member: 'specialtyName',
        sortDirection: 0,
        displayContent: 'Key',
      },
    ];
  };

  const getRightChartGrouping = (isAlignment: boolean) => {
    return [
      {
        member: isAlignment ? 'AlignmentScore' : 'ResponseOption',
        sortDirection: 0,
        displayContent: 'Key',
      },
    ];
  };

  const onExport = () => {
    try {
      setExportingData(true);
      exportMultiQuestionPPT({
        apiUrl: apiUrlFor(API_PREFIX.NEXTGEN),
        questions: questionsForExport(),
        leftChartJson: leftChart,
        rightChartJson: rightChart,
        summaryBar: summaryWidget,
        oppositeLeftChart: oppositeLeftChart,
        oppositeRightChart: oppositeRightChart,
        oppositeSummaryBar: oppositeSummaryWidget,
      });
    } catch (e) {
      console.error('Error exporting PowerPoint:', e);
      dispatch(
        showToast({
          id: 'ppt-export-notification',
          text: `Error generating your PowerPoint. Please try again. If the error persists, please contact
support@kwello.com`,
          hasButton: false,
          type: NotificationType.ERROR,
        })
      );
    } finally {
      setExportingData(false);
      onClose();
    }
  };

  const onSelectAllClick = () => {
    const enabledIds = questionsList.filter((q) => !q.isDisabled).map((q) => q.id);

    if (!allSelected) {
      setSelected(enabledIds);
    } else {
      setSelected([]);
    }
  };

  const stableFilterLabels = useMemo(() => {
    return JSON.stringify(filterLabels);
  }, [filterLabels]);

  useEffect(() => {
    if (!alignmentQuery.isLoading && !insightsQuery.isLoading) {
      const parsedFilterLabels: FilterLabelItem[] = JSON.parse(stableFilterLabels || '[]');

      const allFilters: SimpleFilter[] = [
        ...chartFilters.map((f) => ({
          property: f.propertyName,
          value: typeof f.value === 'string' || typeof f.value === 'number' ? f.value : null,
        })),
        ...parsedFilterLabels.flatMap((f) =>
          f.value.map((v) => ({
            property: FILTER_LABEL_TO_PROPERTY[f.label] || f.label.toLowerCase(),
            value: v,
          }))
        ),
      ];

      const activeQuestions = new Set(
        allFilters
          .filter((f) => f.property?.toLowerCase() === 'question' && !!f.value)
          .map((f) => f.value)
      );
      const activeCategories = new Set(
        allFilters
          .filter((f) => f.property?.toLowerCase() === 'category' && !!f.value)
          .map((f) => f.value)
      );
      const hasAnyActiveFilter = activeQuestions.size > 0 || activeCategories.size > 0;

      setTree(
        data.map((category: any): TreeItem => {
          const categoryName = category.name;
          const isCategoryActive = activeCategories.has(categoryName);

          const hasActiveQuestions = activeQuestions.size > 0;

          const mappedQuestions: QuestionModel[] = category.questions.map((q: QuestionModel) => {
            const isQuestionActive = activeQuestions.has(q.name);

            const isDisabled = hasAnyActiveFilter
              ? hasActiveQuestions
                ? !isQuestionActive
                : !isCategoryActive
              : false;

            return {
              ...q,
              selected: !isDisabled && selected.includes(q.id),
              name: isDisabled ? (
                <HoverableSpan
                  tooltipId={`disabled-q-tip-${q.id}`}
                  tooltipContent={
                    <div style={{ lineHeight: '1.4' }}>
                      <strong style={{ display: 'block', marginBottom: '4px' }}>
                        This question is unavailable based on your current filters.
                      </strong>
                      If you would like to select this item, please adjust your applied page
                      filters.
                    </div>
                  }
                  disabled
                >
                  {q.name}
                </HoverableSpan>
              ) : (
                q.name
              ),
            };
          });

          const hasActiveChild = mappedQuestions.some((q) => !React.isValidElement(q.name));

          const isDisabledCategory = hasAnyActiveFilter
            ? hasActiveQuestions
              ? !hasActiveChild
              : !isCategoryActive
            : false;

          return {
            ...category,
            expanded: expanded.includes(category.id),
            selected:
              !isDisabledCategory &&
              category.questions.every((q: QuestionModel) => selected.includes(q.id)),
            name: isDisabledCategory ? (
              <HoverableSpan
                tooltipId={`disabled-cat-tip-${category.id}`}
                tooltipContent={
                  <div style={{ lineHeight: '1.4' }}>
                    <strong style={{ display: 'block', marginBottom: '4px' }}>
                      This category is unavailable based on your current filters.
                    </strong>
                    If you would like to select this item, please adjust your applied page filters.
                  </div>
                }
                disabled
              >
                {category.name}
              </HoverableSpan>
            ) : (
              category.name
            ),
            questions: mappedQuestions,
          } as TreeItem;
        })
      );
    }
  }, [
    data,
    expanded,
    selected,
    alignmentQuery.isLoading,
    insightsQuery.isLoading,
    stableFilterLabels,
    chartFilters,
  ]);

  return (
    <FlyoutBase rootClassName="flyout-paginated-ppt-export" onOverlayClick={onClose}>
      <FlyoutHeader
        title={'Multiple Question Export'}
        onCloseClick={onClose}
        icon={KWELLO_ICON.BACK_ICON}
        onLeftIconClick={() => dispatch(setWizardStep(WizardStep.PowerpointExportChooseOption))}
      />
      <FlyoutBody>
        <Typography.p className="heading">
          Please select the categories and questions that you want to export to Power Point.
        </Typography.p>
        <div className="flex-row justify-start gap-10">
          <span>Applied filters</span>
          <span data-tooltip-id="app-filters-tooltip">
            <KwelloSvgIcon icon={KWELLO_ICON.INFO_ROUNDED_ICON} />
            <Tooltip
              id="app-filters-tooltip"
              place="top"
              positionStrategy={'fixed'}
              style={{ fontSize: '0.8rem' }}
            >
              <b>
                These filters will shape your <br />
                PowerPoint export.{' '}
              </b>
              <br />
              Want to tweak them? <br />
              Just hop back to the Summary page and <br /> adjust your filters
            </Tooltip>
          </span>
        </div>

        <AppliedFilters showTooltip={false} removable={false} textClass="gray-700" />
        <TextBox
          defaultValue={searchFilter}
          onChange={(e: TextBoxChangeEvent) => setSearchFilter(e.value as string)}
          prefix={() => (
            <div className="input-custom-icon-wrapper">
              <KwelloSvgIcon icon={KWELLO_ICON.SEARCH_ICON} />
            </div>
          )}
          placeholder={'Search'}
        />
        <div className="ppt-export-toolbar">
          <div>
            <Checkbox
              label={allSelected ? 'Unselect all' : 'Select all'}
              checked={allSelected}
              onClick={onSelectAllClick}
            />
          </div>
          {toggleQuestionType && (
            <div>
              Include {isAlignmentPath ? 'Insights' : 'Alignment'} Questions{' '}
              <Switch
                onChange={(e: SwitchChangeEvent) => {
                  e.syntheticEvent.stopPropagation();
                  setIncludeOpposite(e.value);
                }}
                onLabel=""
                offLabel=""
                size={'large'}
                className="k-switch-xs"
              />
            </div>
          )}
        </div>
        {(alignmentQuery.isLoading || insightsQuery.isLoading) && <Spinner />}
        {!alignmentQuery.isLoading && !insightsQuery.isLoading && (
          <TreeView
            data={tree}
            checkField="selected"
            checkboxes={true}
            expandIcons={true}
            childrenField="questions"
            textField="name"
            hasChildrenField="questions"
            expandField="expanded"
            size={'small'}
            onCheckChange={onSelectionChange}
            onExpandChange={onExpandChange}
            item={CustomTreeItem}
            className="ppt-treeview"
          />
        )}
      </FlyoutBody>
      <Footer
        onExport={() => onExport()}
        isLoading={exportingData}
        disabled={selected.length === 0}
      />
    </FlyoutBase>
  );
}

export default Question;
